import React, { useState } from 'react';
import {
    Box,
    LinearProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormHelperText,
    Chip,
} from '@mui/material';
import { StyledDropzone } from './Dropzone';
import CancelIcon from '@mui/icons-material/Cancel';
import { apiClient } from '../../helper/api';
import useAuth from '../../context/AuthProvider';
import Swal from 'sweetalert2';
import { GreenBtnColor, PrimaryBlueColor } from '../../helper/theme';

export interface UploadSignatureRequestDocumentsProps {
    open: boolean;
    onClose: () => void;
    signatureRequestId: number;
    onSuccess: () => void;
}

export const UploadSignatureRequestDocuments: React.FC<UploadSignatureRequestDocumentsProps> = ({
    open,
    onClose,
    signatureRequestId,
    onSuccess,
}) => {
    const { user } = useAuth();
    const token = user?.token;
    const [loading, setLoading] = useState(false);
    const [uploadedDocuments, setUploadedDocuments] = useState<File[]>([]);

    const handleClose = () => {
        setUploadedDocuments([]);
        onClose();
    };

    const handleSendToMisc = () => {
        if (uploadedDocuments.length === 0) {
            Swal.fire({
                title: 'Required',
                text: 'Please upload at least one document before sending to MISC.',
                icon: 'warning',
                confirmButtonText: 'OK',
            });
            return;
        }
        Swal.fire({
            title: '<div style="color: white;">Send to MISC</div>',
            html: '<div style="color: white;">Are you sure you want to send these documents to MISC?</div>',
            showCancelButton: true,
            confirmButtonText: 'Yes, send it!',
            cancelButtonText: 'Cancel',
            confirmButtonColor: GreenBtnColor,
            background: PrimaryBlueColor,
        }).then((result) => {
            if (result.isConfirmed) {
                setLoading(true);
                const formData = new FormData();
                uploadedDocuments.forEach((file) => {
                    formData.append('files', file);
                });
                formData.append('signatureRequestId', signatureRequestId.toString());
                apiClient('/SignatureRequests/uploadSignedDocument', {
                    method: 'POST',
                    headers: new Headers({
                        Authorization: `Bearer ${token}`,
                    }),
                    body: formData,
                })
                    .then((response) => {
                        if (!response) return;
                        if (response.status) {
                            Swal.fire({
                                title: 'Documents Sent to MISC',
                                text: 'Documents sent to MISC successfully.',
                                icon: 'success',
                                confirmButtonText: 'OK',
                            });
                            handleClose();
                            onSuccess();
                        } else {
                            Swal.fire({
                                title: 'Error',
                                text: response?.message || 'Failed to send documents to MISC',
                                icon: 'error',
                                confirmButtonText: 'OK',
                            });
                        }
                    })
                    .catch((error) => {
                        console.error('Error sending documents to MISC:', error);
                        Swal.fire({
                            title: 'Error',
                            text: 'Failed to send documents to MISC. Please try again.',
                            icon: 'error',
                            confirmButtonText: 'OK',
                        });
                    })
                    .finally(() => {
                        setLoading(false);
                    });
            }
        });
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
            {loading && <LinearProgress style={{ margin: 2 }} />}
            <DialogTitle className="card-title">Upload Documents</DialogTitle>
            <DialogContent>
                <FormHelperText className="formtexthelper-heading">
                    Select file(s) or drag and drop. You must upload at least one document, then click Send to MISC to complete.
                </FormHelperText>
                <StyledDropzone
                    label="Select File(s) or Drag and Drop"
                    onFileDrop={(files: File[]) => {
                        setUploadedDocuments((prev) => [...prev, ...files]);
                    }}
                />
                {uploadedDocuments.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                        <FormHelperText className="formtexthelper-heading">
                            Selected Files ({uploadedDocuments.length}):
                        </FormHelperText>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {uploadedDocuments.map((file, index) => (
                                <Chip
                                    key={`${file.name}-${index}`}
                                    label={file.name}
                                    onDelete={() => {
                                        setUploadedDocuments((prev) => prev.filter((_, i) => i !== index));
                                    }}
                                    deleteIcon={<CancelIcon />}
                                    color="primary"
                                    variant="outlined"
                                />
                            ))}
                        </Box>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <button className="btn-2 btn d-flex btn-primary search-btn" type="button" onClick={handleClose}>
                    Close
                </button>
                <button
                    className="btn btn-primary active-btn-blue search-btn"
                    type="button"
                    onClick={handleSendToMisc}
                    disabled={uploadedDocuments.length === 0 || loading}
                >
                    Send to MISC
                </button>
            </DialogActions>
        </Dialog>
    );
};
